/**
 * Shared handler for HTTP 403 responses across all authenticated API clients.
 *
 * Uses a Vue reactive object so App.vue can watch it directly — no window events,
 * no timing issues with listener registration.
 *
 * The active guard ensures only one dialog is shown at a time even when multiple
 * concurrent requests all return 403.
 */

import Vue from 'vue'

// Reactive state that App.vue imports and watches directly.
export const sessionGuardState = new Vue({
  data () {
    return {
      active: false,
      handler: null
    }
  }
})

function _clearLocalSession () {
  localStorage.removeItem('explorer_auth')
  localStorage.removeItem('explorer_user')
}

/**
 * For HTTP 200 responses that carry a business-level session-expiry code (e.g. 100001).
 * Shows the same dialog as handleSession403. On Continue, App.vue refreshes the token
 * (saving it to localStorage), then calls retryFn() which re-runs the original request
 * using the fresh token picked up from localStorage via the request interceptor.
 *
 * @param {Function} retryFn  - () => Promise  — called after token refresh, returns the retried data
 * @returns {Promise}
 */
export function handleBusinessSessionExpiry (retryFn) {
  if (sessionGuardState.active) return new Promise(() => {})

  const returnPath = encodeURIComponent(window.location.pathname + window.location.search)

  return new Promise((resolve, reject) => {
    sessionGuardState.handler = {
      onContinue () {
        sessionGuardState.active = false
        sessionGuardState.handler = null
        Promise.resolve(retryFn()).then(resolve).catch(reject)
      },
      onCancel () {
        sessionGuardState.active = false
        sessionGuardState.handler = null
        _clearLocalSession()
        window.location.href = `/login?redirect=${returnPath}`
      }
    }
    sessionGuardState.active = true
  })
}

/**
 * @param {Object} error         - Axios error object from the response interceptor
 * @param {Object} httpClient    - The axios instance that owns this interceptor (used to retry)
 * @returns {Promise}            - Stays pending until the user acts; resolves with the retried
 *                                 response on Continue, or never resolves on Cancel (page navigates away)
 */
export function handleSession403 (error, httpClient) {
  const returnPath = encodeURIComponent(window.location.pathname + window.location.search)

  // If this is already a retried request that still got 403, the refreshed token
  // is also rejected — don't show the dialog again, just redirect to login.
  if (error.config && error.config._403retry) {
    sessionGuardState.active = false
    sessionGuardState.handler = null
    _clearLocalSession()
    window.location.href = `/login?redirect=${returnPath}`
    return new Promise(() => {})
  }

  if (sessionGuardState.active) return new Promise(() => {})

  return new Promise((resolve, reject) => {
    sessionGuardState.handler = {
      onContinue (newAccessToken) {
        sessionGuardState.active = false
        sessionGuardState.handler = null
        // Build a fresh config object — avoids mutating the AxiosHeaders instance
        // on error.config which can leave stale Authorization values on the retry.
        const retryConfig = Object.assign({}, error.config, {
          _403retry: true,
          headers: Object.assign({}, error.config.headers, {
            Authorization: `Bearer ${newAccessToken}`
          })
        })
        httpClient(retryConfig).then(resolve).catch(reject)
      },
      onCancel () {
        sessionGuardState.active = false
        sessionGuardState.handler = null
        _clearLocalSession()
        window.location.href = `/login?redirect=${returnPath}`
      }
    }
    // Set active AFTER handler is assigned so App.vue sees a complete state
    sessionGuardState.active = true
  })
}
